import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { trackBeginCheckout } from "@/lib/analytics";
import { Calendar, Clock, Users } from "lucide-react";

export default function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post("/classes/register", { class_id: selected.id, ...form });
      trackBeginCheckout({
        kind: "class",
        item_id: selected.id,
        item_name: selected.title,
        price: selected.price,
        category: "cooking_class",
      });
      // Then offer Stripe checkout
      const { data } = await api.post("/payments/checkout", {
        kind: "class",
        item_id: selected.id,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Could not start registration. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="classes-page">
      <SEO
        title="Cooking Classes"
        description="Hands-on seafood cooking classes for all levels. Learn filleting, smoking, and chef-led techniques. Book your spot today."
        path="/classes"
      />
      <section className="bg-[color:var(--color-primary)] text-white py-20">
        <div className="container-x px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-4">Hands-on Learning</div>
          <h1 className="font-display text-5xl md:text-6xl">Cooking Classes</h1>
          <p className="text-white/70 max-w-2xl mt-4">Learn to prepare different fish delicacies — from filleting whole fish to smoking, curing, and plating like a pro.</p>
        </div>
      </section>

      <section className="section bg-[color:var(--color-bg)]">
        <div className="container-x">
          <h2 className="font-display text-3xl text-[color:var(--color-primary)] mb-10">Upcoming Classes</h2>
          {classes.length === 0 ? (
            <div className="text-center py-16 text-[color:var(--color-text-secondary)]" data-testid="no-classes">No upcoming classes scheduled. Check back soon.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {classes.map((c) => (
                <div key={c.id} className="bg-white border border-[color:var(--color-border)] rounded-lg overflow-hidden flex flex-col" data-testid={`class-card-${c.id}`}>
                  <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                    {c.image && <img src={c.image} alt={c.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-display text-2xl text-[color:var(--color-primary)] mb-2">{c.title}</h3>
                    <p className="text-sm text-[color:var(--color-text-secondary)] mb-4 flex-1">{c.description}</p>
                    <div className="text-xs uppercase tracking-widest text-gray-600 space-y-1 mb-4">
                      <div className="flex items-center gap-2"><Calendar className="w-3 h-3"/> {c.date}</div>
                      <div className="flex items-center gap-2"><Clock className="w-3 h-3"/> {c.time}</div>
                      <div className="flex items-center gap-2"><Users className="w-3 h-3"/> {c.seats} seats</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="font-display text-2xl text-[color:var(--color-accent)]">£{c.price.toFixed(2)}</div>
                      <button onClick={() => setSelected(c)} className="btn-accent" data-testid={`class-register-${c.id}`}>Register</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="class-modal">
          <div className="bg-white rounded-lg p-8 w-full max-w-md">
            <h2 className="font-display text-3xl text-[color:var(--color-primary)] mb-1">Register for {selected.title}</h2>
            <p className="text-sm text-gray-500 mb-6">£{selected.price.toFixed(2)} · {selected.date} at {selected.time}</p>
            <form onSubmit={submit} className="space-y-4">
              {[
                { name: "name", label: "Full Name", type: "text" },
                { name: "email", label: "Email", type: "email" },
                { name: "phone", label: "Phone", type: "tel" },
              ].map((f) => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest text-gray-600">{f.label}</label>
                  <input
                    required type={f.type}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2 focus:outline-none focus:border-[color:var(--color-primary)]"
                    data-testid={`class-input-${f.name}`}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setSelected(null)} className="btn-ghost flex-1 justify-center" data-testid="class-cancel-btn">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-accent flex-1 justify-center" data-testid="class-submit-btn">
                  {submitting ? "Loading…" : "Register & Pay"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
